import React, { useMemo, useState } from 'react';
import { type TeasersFieldValue, type PortalSettings } from '@gorilla/portal-shared/src/schemas';
import { type TeasersFieldDescriptor } from '@gorilla/portal-shared/src/types';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PagePickerModal } from '../PagePickerModal';
import { getSwatchThemeStyle, SwatchSetting } from '../SwatchSetting';
import { IconButton, TextField, Box, Flex, Text, Icon, Button } from '@vibe/core';
import { Delete, AddSmall, CloseSmall, Link, Drag } from '@vibe/icons';

const isExternalUrl = (url?: string) => /^(https?:\/\/|mailto:|tel:)/i.test((url ?? '').trim());
const getTextFieldValue = (evOrValue: any) => (typeof evOrValue === 'string' ? evOrValue : (evOrValue?.target?.value ?? ''));

const makeRowId = () => `teaser-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

type TeasersFieldProps = {
  portalSettings: PortalSettings;
  value: TeasersFieldValue | undefined;
  fieldDescriptor: TeasersFieldDescriptor;
  onChange: (value: TeasersFieldValue | undefined) => void;
  pageId: string;
};

type TeaserItem = NonNullable<TeasersFieldValue>[number];

type SortableTeaserRowProps = {
  id: string;
  teaser: TeaserItem;
  index: number;
  isOnly: boolean;
  teaserIcon: string;
  onUpdate: (index: number, updates: Partial<TeaserItem>) => void;
  onDelete: (index: number) => void;
  onOpenPagePicker: (index: number) => void;
};

function SortableTeaserRow({ id, teaser, index, isOnly, teaserIcon, onUpdate, onDelete, onOpenPagePicker }: SortableTeaserRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isOnly,
  });

  const hasExternalUrl = isExternalUrl(teaser.url);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 1000 : undefined,
    position: isDragging ? ('relative' as const) : undefined,
    width: '100%',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Box style={{ width: '100%' }}>
        <Flex gap={14} align="end">
          <div style={{ width: '18px' }}>
            <div
              className="custom__teaser__draghandle"
              {...attributes}
              {...listeners}
              style={{
                cursor: isOnly ? 'default' : isDragging ? 'grabbing' : 'grab',
                opacity: isOnly ? 0.4 : 1,
              }}
            >
              <Icon icon={Drag} size={18} />
            </div>
          </div>

          <div style={{ position: 'relative', width: '25%' }}>
            <TextField
              size="medium"
              value={teaser.headline || ''}
              onChange={(newValue) => {
                onUpdate(index, { headline: getTextFieldValue(newValue) || '' });
              }}
              className="page__icon__picker__input--valid__right"
            />
            {teaser.url ? (
              <div
                style={{
                  pointerEvents: 'none',
                  position: 'absolute',
                  top: '50%',
                  right: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  transform: 'translate3d(0,-50%,0)',
                  opacity: teaser.headline ? 1 : 0.4,
                  fontSize: '14px',
                }}
              >
                <i className={`fa-regular fa-${teaserIcon}`}></i>
              </div>
            ) : null}
          </div>

          <div style={{ flex: 1, minWidth: '120px' }}>
            <TextField
              size="medium"
              value={teaser.text || ''}
              onChange={(newValue) => {
                onUpdate(index, { text: getTextFieldValue(newValue) || '' });
              }}
            />
          </div>

          <div style={{ width: '25%' }}>
            <TextField
              size="medium"
              value={teaser.url || ''}
              placeholder="https:// or /page/alias"
              onChange={(newValue) => {
                onUpdate(index, { url: getTextFieldValue(newValue) || '' });
              }}
              icon={hasExternalUrl ? CloseSmall : Link}
              clearOnIconClick={hasExternalUrl}
              onIconClick={
                hasExternalUrl
                  ? undefined
                  : () => {
                      onOpenPagePicker(index);
                    }
              }
            />
          </div>

          <div style={{ width: '64px' }}>
            <SwatchSetting
              color={teaser.backgroundColor}
              onChange={(newColor) => {
                onUpdate(index, { backgroundColor: newColor });
              }}
            />
          </div>

          <div>
            <IconButton
              icon={Delete}
              kind="tertiary"
              size="medium"
              disabled={isOnly}
              onClick={() => {
                onDelete(index);
              }}
              className="custom__deletepagebutton"
            />
          </div>
        </Flex>
      </Box>
    </div>
  );
}

export function TeasersField({ portalSettings, value, onChange, pageId }: TeasersFieldProps) {
  const [pagePickerIndex, setPagePickerIndex] = useState<number | null>(null);
  const [rowIds, setRowIds] = useState<string[]>(() => (value ?? []).map(() => makeRowId()));

  const teasers = value ?? [];
  const selectedUrl = pagePickerIndex != null ? teasers[pagePickerIndex]?.url : undefined;
  const selectedPageId = selectedUrl
    ? portalSettings.pages.find((page) => `/page/${page.id}` === selectedUrl.split('#')[0])?.id
    : undefined;
  const EXTERNAL_ICON = 'arrow-up-right-from-square';
  const INTERNAL_FALLBACK_ICON = 'arrow-right';

  const getPageIcon = (url?: string) => {
    const normalizedUrl = (url ?? '').trim();

    if (isExternalUrl(normalizedUrl)) {
      return EXTERNAL_ICON;
    }

    const [pageUrl, anchor] = normalizedUrl.split('#');

    const page = portalSettings.pages.find((page) => `/page/${page.id}` === pageUrl);

    if (!page) {
      return INTERNAL_FALLBACK_ICON;
    }

    const isAnchorOnCurrentPage = Boolean(anchor) && page.id === pageId;

    return isAnchorOnCurrentPage ? INTERNAL_FALLBACK_ICON : page.icon || INTERNAL_FALLBACK_ICON;
  };

  const updateTeaser = (index: number, updates: Partial<TeaserItem>) => {
    const newTeasers = [...teasers];
    newTeasers[index] = {
      ...newTeasers[index],
      ...updates,
    };
    onChange(newTeasers);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const sortableItems = useMemo(
    () => (rowIds.length === teasers.length ? rowIds : teasers.map(() => makeRowId())),
    [rowIds, teasers.length],
  );

  const handleAdd = () => {
    setRowIds([...sortableItems, makeRowId()]);
    onChange([...teasers, { headline: '', text: '', url: '' }]);
  };

  const handleDelete = (index: number) => {
    setRowIds(sortableItems.filter((_, i) => i !== index));
    onChange(teasers.filter((_, i) => i !== index));
  };

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = sortableItems.findIndex((id) => id === active.id);
    const newIndex = sortableItems.findIndex((id) => id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    setRowIds(arrayMove(sortableItems, oldIndex, newIndex));
    onChange(arrayMove(teasers, oldIndex, newIndex));
  }

  return (
    <Box className="section__field section__field--teasers" style={{ overflow: 'visible', ...getSwatchThemeStyle(portalSettings.theme) }}>
      {pagePickerIndex != null ? (
        <PagePickerModal
          title="Link to portal page"
          icon={Link}
          description="Choose the page this teaser card links to in the client portal."
          selectedPageId={selectedPageId}
          portalSettings={portalSettings}
          isOpen
          onClose={() => {
            setPagePickerIndex(null);
          }}
          onSelect={(page) => {
            if (pagePickerIndex == null) return;

            updateTeaser(pagePickerIndex, { url: `/page/${page.id}` });
            setPagePickerIndex(null);
          }}
        />
      ) : null}

      <Box style={{ marginTop: '-6px' }}>
        <Flex gap={14} direction="column" style={{ width: '100%' }}>
          {teasers.length > 0 ? (
            <>
              <Box style={{ width: '100%' }}>
                <Flex gap={14} align="end">
                  <div style={{ pointerEvents: 'none', width: '18px' }}>
                    <Text type="text2" color="primary">
                      &nbsp;
                    </Text>
                  </div>

                  <div style={{ pointerEvents: 'none', width: '25%' }}>
                    <Text type="text2" color="primary">
                      Headline + Icon
                    </Text>
                  </div>

                  <div style={{ pointerEvents: 'none', flex: 1, minWidth: '120px' }}>
                    <Text type="text2" color="primary">
                      Text
                    </Text>
                  </div>

                  <div style={{ pointerEvents: 'none', width: '25%' }}>
                    <Text type="text2" color="primary">
                      URL
                    </Text>
                  </div>

                  <div style={{ pointerEvents: 'none', width: '64px' }}>
                    <Text type="text2" color="primary">
                      Color
                    </Text>
                  </div>

                  <div style={{ pointerEvents: 'none', width: '40px' }}>
                    <Text type="text2" color="primary">
                      &nbsp;
                    </Text>
                  </div>
                </Flex>
              </Box>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
                  <Flex gap={14} direction="column" style={{ width: '100%' }}>
                    {teasers.map((teaser, index) => {
                      const id = sortableItems[index];
                      if (!id) return null;

                      return (
                        <SortableTeaserRow
                          key={id}
                          id={id}
                          teaser={teaser}
                          index={index}
                          isOnly={teasers.length === 1}
                          teaserIcon={getPageIcon(teaser.url)}
                          onUpdate={updateTeaser}
                          onDelete={handleDelete}
                          onOpenPagePicker={(pickerIndex) => {
                            setPagePickerIndex(pickerIndex);
                          }}
                        />
                      );
                    })}
                  </Flex>
                </SortableContext>
              </DndContext>

              <Box style={{ width: '100%', textAlign: 'right' }}>
                <IconButton icon={AddSmall} kind="primary" size="medium" onClick={handleAdd} />
              </Box>
            </>
          ) : (
            <Box style={{ width: '100%', textAlign: 'right' }}>
              <IconButton icon={AddSmall} kind="primary" size="medium" onClick={handleAdd} />
            </Box>
          )}
        </Flex>
      </Box>
    </Box>
  );
}
